import React, { useEffect, useState, useRef } from "react";
import { socket } from "../../../lib/socketio";
import { getSeconds } from "../../../lib/time";
import useInterval from "../../../customHooks/useInterval";
import {
  Dropdown,
  Button,
  Input,
  Grid,
  Tab,
  Dimmer,
  Form,
} from "semantic-ui-react";
import {
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
} from "recharts";

const LiveChartsPlot = ({
  chartList,
  setChartList,
  tags,
  setTags,
  tagsArr,
  data,
  setData,
  key,
  id,
}) => {
  const [currentValue, setCurrentValue] = useState([]); // selectedValue Array
  const [chartValue, setChartValue] = useState([]); // 차트에 표시되는 값 raw data
  const [isDraw, setIsDraw] = useState(false); // isDraw ? useInterval 실행 : 실행X

  const [groupTemp, setGroupTemp] = useState([]); // selected Value from dropdown box
  const groupIndex = useRef(2);
  const group = "Group";
  const [groupList, setGroupList] = useState(["Group1"]);
  const [groupName, setGroupName] = useState(""); // Dropdown의 groupName (Group1 Dropdown, Group2 Dropdown...)
  const [isGroup, setIsGroup] = useState(false); // custom onClick

  const [lineColor, setLineColor] = useState([]); // stroke random line color
  const [opacity, setOpacity] = useState({}); // set strokeOpacity
  const idValue = useRef(null);
  const dataLength = useRef(0);
  const [selectedTagOptions, setSelectedTagOptions] = useState([]);

  const [saveFilePath, setSaveFilePath] = useState("");
  const [loadFilePath, setLoadFilePath] = useState("");
  const [currentFileName, setCurrentFileName] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const [readConfig, setReadConfig] = useState([]);

  const [xInput, setXInput] = useState({
    xInterval: 1000, // 현 delay 값
    xAxisTime: 5, // 현 domainValue 값
  });
  const { xInterval, xAxisTime } = xInput;

  const [yInput, setYInput] = useState({
    yMin: "auto",
    yMax: "auto",
  });
  const { yMin, yMax } = yInput;

  const date = new Date();
  const ids = tags.map((t) => t.id);

  useEffect(() => {
    if (ids.length > 0) {
      socket.emit("get tag values", ids);
      socket.on("receive tag values", (value) => {
        setData(value.result);
      });
      return () => {
        socket.removeAllListeners();
      };
    }
  }, [ids, data]);

  const getSaveFilePath = () => {
    let fileName = "ChartConfig_";
    const d = new Date();
    const datestring =
      d.getFullYear() +
      "-" +
      (d.getMonth() + 1) +
      "-" +
      d.getDate() +
      " " +
      d.getHours() +
      "-" +
      d.getMinutes();
    fileName += datestring.replace(/ /g, "_");
    setSaveFilePath(fileName);

    return fileName;
  };

  const getTextValue = (tempId) => {
    let res = Object.values(tagsArr).find((c) => c.value === tempId).text;
    return res;
  };

  const dropdownDelete = (selectedValue) => {
    if (
      !isDraw &&
      dataLength.current === 0 &&
      Object.values(chartValue).length < 1
    ) {
      setGroupTemp([]); // group에 넘어가는 현재 선택된 값
      setOpacity([]); // line opacity
      setCurrentValue([]); // 현재 선택된 값 tags : value
      setLineColor([]); // 현재 선택된 값 기반 random lineColor
    } else if (isDraw && dataLength.current === 0) {
      allClearClick();
    } else {
      setGroupTemp(
        groupTemp.filter(
          (c) => typeof c !== "undefined" && selectedValue.includes(c.value),
        ),
      ); // groupTemp값 변경

      let arr = Object.values(groupTemp).map((c) => c.value);
      let diff = arr.filter((x) => !selectedValue.includes(x));
      let deleteTemp = getTextValue(Number(diff));

      delete currentValue[deleteTemp]; // currentValue 값 변경
      setCurrentValue(currentValue);
      delete opacity[deleteTemp]; // opacity 값 변경
      setOpacity(opacity);
    }
  };

  //^ handleTagsChange는 ChooseTags의 값이 변경 될 때, 일어나야 하는 로직만 일어나야함.
  //^ drawChanrt 함수를 따로 빼는게 나을 것 같음

  const handleTagsChange = (e, selectedData) => {
    // ^ config에서 값을 불러오는 경우에 대한 조건 설정 필요함.
    if (Object.values(readConfig).length > 0) {
      // json 파일 업로드 한 경우,
      dataLength.current = Object.values(readConfig).length;
    } else {
      dataLength.current = selectedData.value.length;
    }

    var lastText;

    if (dataLength.current > 0) {
      var lastId = Number(selectedData.value[selectedData.value.length - 1]); // 가장 최근에 선택된 tag에 대한 id와 data -> last Id, last Data
      var lastData = data[lastId];
      lastText = getTextValue(lastId);
    }

    setCurrentValue({
      ...currentValue,
      [lastText]: lastData,
      timestamp: getSeconds(date),
    });
    setOpacity({ ...opacity, [lastText]: 1 });

    // * group선택 위한 groupTemp 생성
    if (Object.keys(tagsArr).length > 0) {
      setGroupTemp([
        ...groupTemp,
        {
          key: lastId,
          text: lastText,
          yAxisId: "",
          value: lastId,
          value1: data[lastId],
        },
      ]);
    }

    for (let i = 0; i < dataLength.current; i++) {
      setLineColor([...lineColor, randomColor()]);
    }

    if (selectedData.value.length < Object.values(currentValue).length) {
      dropdownDelete(selectedData.value);
    }
    setSelectedTagOptions(selectedData.value);
  };

  useInterval(() => {
    if (isDraw) {
      let tempDomain = (xAxisTime * 60) / (xInterval / 1000); // update 220614, domain min단위로 변경 ->

      if (chartValue.length >= tempDomain) {
        if (tempDomain < chartValue.length) {
          chartValue.splice(0, chartValue.length - tempDomain);
        }
        getValue(); // ppc 값 실시간으로 가져옴
        setCurrentValue({ ...currentValue, timestamp: getSeconds(date) }); // currentValue PPC에서 값 가져와야함
        setChartValue([...chartValue, currentValue]);
      } else {
        getValue(); // ppc 값 실시간으로 가져옴
        setCurrentValue({ ...currentValue }); // currentValue PPC에서 값 가져와야함
        setChartValue([...chartValue, currentValue]);
      }
    }
  }, xInterval);

  // 실시간 값 가져오기
  const getValue = () => {
    let tempObj = Object.keys(currentValue);
    var resArray = [];

    for (let i = 0; i < tempObj.length; i++) {
      let temp = tempObj[i];
      if (Object.keys(tagsArr).length > 0) {
        if (temp === "timestamp") {
          resArray = [...resArray, getSeconds(date)];
        } else {
          resArray = [
            ...resArray,
            ...tagsArr.filter((c) => c.text === temp).map((c1) => c1.value1),
          ];
        }
      } else {
        if (temp === "timestamp") {
          resArray = [...resArray, getSeconds(date)];
        } else {
          resArray = [
            ...resArray,
            ...tagsArr.filter((c) => c.text === temp).map((c1) => c1.value1),
          ];
        }
      }
    }

    tempObj.map((c, i) => {
      c === "timestamp"
        ? (currentValue[c] = getSeconds(date))
        : (currentValue[c] = resArray[i]);
      setCurrentValue(currentValue);
    });
  };

  const handleDrawClick = (e) => {
    e.preventDefault();
    // * Draw 버튼 최초 클릭
    dataLength.current !== 0 && !isDraw && setIsDraw(true);

    if (Object.values(loadFilePath).length > 0) {
      setLoadFilePath("");
    }
  };

  const LineClearClick = (e) => {
    e.preventDefault();
    setChartValue([]);
    //! isDraw일때만 누를 수 있게 조건 필요함. 조건 없을 경우 클릭 시 계속 렌더링 됨.
  };

  // chart Clear
  const allClearClick = () => {
    // e.preventDefault();
    setCurrentValue([]);
    setChartValue([]);
    setOpacity([]);
    setLineColor([]);
    setIsDraw(false);
    setIsGroup(false);
    setGroupTemp([]);
    setGroupList(["Group1"]);
    groupIndex.current = 2;
    setSelectedTagOptions([]); // update 220628
    setYInput({
      //update 220817 : yAxis Min/Max Custom
      yMin: "auto",
      yMax: "auto",
    });
    setXInput({
      //update 220817 : 비구조화 할당
      xAxisTime: 5,
      xInterval: 1000,
    });
    setSaveFilePath("");
    setLoadFilePath("");
    setCurrentFileName("");
    setErrMsg("");
    setReadConfig("");
  };

  // * config file upload 전 Clear 되어야하는 항목들 Clear하는 함수
  const uploadClear = () => {
    setCurrentValue([]);
    setChartValue([]);
    setOpacity([]);
    setLineColor([]);
    setIsDraw(false);
    setIsGroup(false);
    setGroupTemp([]);
    setGroupList(["Group1"]);
    setGroupList([]);
    groupIndex.current = 1; // update 221011
    setSelectedTagOptions([]); // update 220628
    setYInput({
      //update 220817 : yAxis Min/Max Custom
      yMin: "auto",
      yMax: "auto",
    });
    setXInput({
      xAxisTime: 5,
      xInterval: 1000,
    });
    setLoadFilePath("");
    setCurrentFileName("");
  };

  const randomColor = (cnt) => {
    let randomStroke = "#";

    for (let i = 0; i < 3; i++) {
      randomStroke += (
        "0" +
        Math.floor(((1 + Math.random()) * Math.pow(16, 2)) / 2).toString(16)
      ).slice(-2);
    }

    // for(let i =0; i < cnt; i++){
    //     setLineColor([...lineColor, randomStroke]);

    // }
    return randomStroke;
  };

  // get groupName
  const groupDropdownClick = (e) => {
    e.preventDefault();
    setGroupName([
      Object.values(e.target.classList).filter((c) => c.includes("Group") && c),
    ]);
  };

  // undefined로 값 들어가는 경우 확인 필요함.
  const groupChange = (e, selectedData) => {
    e.preventDefault();

    let temp = selectedData.value; //현재 선택 된 데이터
    groupTemp
      .filter((x) => temp.includes(x.value))
      .map((c) => (c.yAxisId = groupName)); // yAxisId가 group으로설정된값 filter
    setGroupTemp(groupTemp);

    //* 선택했던 태그에서 삭제
    if (e.target.className.includes("delete") && !isGroup) {
      // update 220623, !isGroup 조건 추가함
      // groupTemp.filter(x => !temp.includes(x.value)).map(c => c.yAxisId = ''); //삭제 버튼 클릭시, 해당 데이터는 yAxis값 초기화.
      groupTemp.map((x) =>
        x.text === e.target.parentNode.textContent ? (x.yAxisId = "") : "",
      ); // ? 수정필요 : ?조건문 대신 && 사용하면 안되는지 확인 필요
    }

    // ? 수정필요
    groupTemp.map((c) => (c.yAxisId = c.yAxisId.toString()));

    // ? 수정필요 아래는 삭제해도 되는 코드로 보임
    groupList.map((c, i) => {
      groupTemp.filter((x) => x.yAxisId.includes(c) && console.log(x.text));
    });
  };

  const groupApplyClick = (e) => {
    // custom Submit Click
    // e.preventDefault(); // * handleLoadClick에서 사용하려면 e.preventDefault 없어야함.
    setIsGroup(true);
    // ^ config file을 업로드 한 후에 isGroup(true)을 적용하는 경우, groupIndex등 group관련 state값 재설정 필요함.
    if (Object.values(readConfig).length > 0) {
      let groupNameArray = groupTemp.map((c) => c.yAxisId);
      groupNameArray = groupNameArray.sort();
    }
  };

  // group Add Button
  const addGroupBtnClick = (e) => {
    e.preventDefault();
    setGroupList([...groupList, group + groupIndex.current]);
    groupIndex.current += 1;
    // isGroup && setIsGroup(false);
  };

  const groupClearClick = (e) => {
    e.preventDefault();
    setGroupList(["Group1"]);
    groupIndex.current = 2;
    groupTemp.filter((x) => x.text !== "").map((c) => (c.yAxisId = ""));
    setIsGroup(false);
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    // chartList.length >= 1 && setChartList(chartList.filter(c => chartList.id !== c.id));
    setChartList(chartList.filter((c) => c !== idValue.current.id));
  };

  // update 220623, useCallback apply
  const renderLegendClick = (e) => {
    if (opacity[e.target.textContent] === 1) {
      e.target.style.opacity = 0.3;
      setOpacity({ ...opacity, [e.target.textContent]: 0.2 });
    } else {
      e.target.style.opacity = 1;
      setOpacity({ ...opacity, [e.target.textContent]: 1 });
    }
  };

  const legendFormatter = (value, o) => {
    const styleLegend = {
      opacity: 1,
      width: "280px",
    };

    return (
      <>
        <span
          className="legend-item"
          onClick={renderLegendClick}
          style={styleLegend}
        >
          {value}
        </span>

        <span style={{ float: "right", width: "50px", marginLeft: "10px" }}>
          {currentValue[value]}
        </span>

        {/* 
                <span style={{float : "left", width : '50px', marginLeft : '10px'}}>
                    {currentValue[value]}
                </span>


                <span className="legend-item" onClick={renderLegendClick} style={styleLegend} > 
                    {value}
                </span> */}
      </>
    );
  };

  // update 220816 YAxis Min/Max Custom
  const handleYInputChange = (e) => {
    const { value, name } = e.target;
    setYInput({
      ...yInput,
      [name]: "",
    });

    setYInput({
      ...yInput,
      [name]: Number(value),
    });

    if (value.length === 0) {
      setYInput({
        ...yInput,
        [name]: "",
      });
    }
  };

  // update 220816 XAxis Custom 수정, Interval & xAxisTime 값 조건문 추가 필요
  const handleXAxisChange = (e) => {
    const { value, name } = e.target;
    setXInput({
      ...xInput,
      [name]: Number(value),
    });
  };

  const handleSaveClick = () => {
    // ! browser check 필요함
    // var fs = window.require('fs');
    let json_string = JSON.stringify(groupTemp);
    let uri = "data:text/csv;charset=utf-8," + escape(json_string);
    let link = document.createElement("a");
    link.href = uri;
    link.style = "visibility:hidden";
    link.download = getSaveFilePath() + ".json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveFilePathChange = (e, { value }) => {
    setSaveFilePath(value);
  };

  const handleLoadClick = () => {
    if (Object.values(readConfig).length > 0) {
      uploadClear(); // 이전 내용 삭제

      // ^ dataCurrent.length 생성
      dataLength.current = Object.values(readConfig).length;

      // ^ randomColor
      for (let i = 0; i < dataLength.current; i++) {
        lineColor.push(randomColor());
        setLineColor(lineColor);
      }

      // ^ selectedTagOptions 생성
      setSelectedTagOptions(Object.values(readConfig).map((c) => c.key));

      // ^ currentValue 생성
      Object.values(readConfig).map((c) =>
        setCurrentValue((currentValue[c.text] = c.value1)),
      );
      setCurrentValue({ ...currentValue, timestamp: getSeconds(date) });

      // ^ opacity 생성
      // * 220927 opacity 값 추가 부분 수정 -> 테스트 필요함.
      for (let i = 0; i < dataLength.current; i++) {
        setOpacity({ [readConfig[i].text]: 1 });
      }
      setOpacity(opacity);
      // ^ groupTemp 생성
      setGroupTemp(readConfig);

      // ! setIsDraw를 할 경우, 조건 필요함. ->?
      setIsDraw(true);

      // ^ 현재 업로드한 파일 화면에 표시
      let fileNameArray = loadFilePath.split("\\");
      setCurrentFileName(fileNameArray[fileNameArray.length - 1]);
      setLoadFilePath(""); // * loadFilePath 초기화 하지 않을 경우, 다른 탭으로 넘어갔다가 다시 Settings tab 진입하여 렌더링 될 때, Value 오류 발생해서 초기화 코드 추가함.

      // ^ Group설정 값 존재 할 경우 동작 -> group값 존재 여부 아래에서 판단 후 함수 호출
      let getYAxisValue = readConfig.map((c) => c.yAxisId);
      if (getYAxisValue.includes("Group1")) {
        configGroup(readConfig);
      }
    }
  };

  //* config file upload할 경우, config 파일에 group 설정 관련 내용 존재할 경우 setting
  const configGroup = (obj) => {
    let tempArray = obj.map((c) => c.yAxisId).filter((c) => c !== ""); // group name만 추출하여 값 정렬

    // ^ groupList 값 생성
    let resArray = tempArray.filter((c, i) => {
      return tempArray.indexOf(c) === i;
    });

    resArray = resArray.sort();
    setGroupList(resArray);

    // ^ groupIndex 생성
    let lastIndex = Number(resArray[resArray.length - 1].slice(5));
    groupIndex.current = lastIndex + 1;

    groupApplyClick();

    // * yAxisId 값 중에 Group1이 포함되어 있을 경우, -> group 설정 된 항목이 하나 이상 존재하는 경우
    // if(tempArray.includes('Group1')){
    //     groupApplyClick();
    // }

    // let last = sortArray[sortArray.length -1];
    // ^ groupIndex(useRef)값 넣어야함
    // let lastIndex = Number(last.slice(5));
    // groupIndex.current = lastIndex;

    // ^ groupName ? 은 모르겠음
    // ^ isGroup (true) -> groupApplyClick() 호출해도 됨.
    // ^ GroupName 대소비교 하려면 localeCompare 사용하면 됨.
  };

  const handleLoadChange = (e, { value }) => {
    //handleLoadChange
    setLoadFilePath(value);

    let file = e.target.files[0];
    let fileReader = new FileReader();
    var fileName = e.target.files[0].name.split(".");
    setCurrentFileName(fileName[0]);

    // ! file확장자가 json일 경우만 업로드 되어야하며, 빈 파일일 경우 업로드 되지 않아야함.
    if (fileName[fileName.length - 1] === "json") {
      fileReader.onload = () => {
        let res = fileReader.result;
        if (res.length > 0) {
          // JSON.parse(res).length로 할 경우, 빈 파일일 경우 파싱자체가 안됨.
          setReadConfig(JSON.parse(res));
          setErrMsg("");
        } else {
          // * alert msg : 빈 파일입니다.
          setReadConfig("");
          setLoadFilePath("");
          setCurrentFileName("");
          setErrMsg("빈 파일입니다.");
        }
      };
      fileReader.readAsText(file);
    } else {
      setLoadFilePath("");
      setReadConfig("");
      setErrMsg("파일 형식이 유효하지 않습니다.");
      // * alert msg : 파일 형식이 유효하지 않습니다.
    }

    // ! 파일이 업로드 되어 있는데 또 업로드 하는 경우,
  };

  const panes = [
    {
      menuItem: "Tags",
      render: () => (
        <Tab.Pane
          attached={false}
          style={{ backgroundColor: "#0c0c0c", color: "aliceblue" }}
        >
          <Dimmer active={tags.length === 0} style={{ width: "260px" }}>
            {" "}
            Loading...{" "}
          </Dimmer>
          <p style={{ fontSize: "18px" }}>Choose Tags</p>
          <div
            className="tagsButton"
            style={{ width: "235px", marginBottom: "10px" }}
          >
            <Button inverted color="olive" onClick={handleDrawClick}>
              {" "}
              Draw{" "}
            </Button>
            <Button
              inverted
              color="yellow"
              style={{ marginLeft: "10px" }}
              onClick={LineClearClick}
            >
              {" "}
              Data Clear{" "}
            </Button>
          </div>
          <Dropdown
            class="ChooseTags"
            multiple
            search
            selection
            options={Object.keys(tagsArr).length > 0 ? tagsArr : ""}
            margin={{ top: 5, right: 30, left: 20, bottom: 10 }}
            onChange={handleTagsChange}
            style={{ width: "235px" }}
            key={key}
            // selectedLabel
            value={selectedTagOptions}
            lazyLoad={true}
            basic={true}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Axes",
      render: () => (
        <Tab.Pane
          attached={false}
          style={{ backgroundColor: "#0c0c0c", color: "aliceblue" }}
        >
          <h3 style={styles.subTitleTop}>X-Axis</h3>
          <Input
            onChange={handleXAxisChange}
            placeholder="Interval"
            step="1000"
            min="1000"
            max="5000"
            type="number"
            defaultValue={xInterval}
            labelPosition="right"
            style={{ width: "235px" }}
            action={{
              content: "Interval",
            }}
            actionPosition="left"
            label={{ basic: true, content: "ms" }}
            name="xInterval"
            autoComplete="off"
          />
          <br />
          <Input
            onChange={handleXAxisChange}
            type="number"
            max="30" // 220614 sec to min 변경
            min="1"
            step="1"
            defaultValue={xAxisTime}
            action={{
              content: "XAxis Time",
            }}
            actionPosition="left"
            label={{ basic: true, content: "min" }}
            labelPosition="right"
            style={styles.inputBox}
            name="xAxisTime"
            autoComplete="off"
          />
          <br />

          <p style={styles.subTitleMid}>
            {" "}
            Y-Axis
            <Button
              content="Auto"
              onClick={() => {
                setYInput({ yMin: "auto", yMax: "auto" });
              }}
              style={{ float: "right", marginBottom: "10px" }}
              inverted
              color="yellow"
            />
          </p>
          {/* <h3 style={{fontColor : 'aliceblue', marginBottom : '10px', marginTop : '20px'}}>Y-Axis</h3> */}

          <div style={{ width: "250px" }}>
            {/*// update 220816 YAxis min/max Custom */}
            <div class="YInput">
              <Input
                onChange={handleYInputChange}
                label="Min"
                style={{ width: "235px" }}
                value={yMin}
                name="yMin"
                type="number"
                placeholder="auto"
                autoComplete="off"
                //disabled={true}
              />
              <Input
                onChange={handleYInputChange}
                label="Max"
                style={styles.inputBox}
                value={yMax}
                name="yMax"
                type="number"
                placeholder="auto"
                autoComplete="off"
              />
            </div>

            <p style={styles.subTitleMid}>Group</p>
            <Button content="Add Group" onClick={addGroupBtnClick} />
            <Button
              content="Clear"
              onClick={groupClearClick}
              style={{ marginLeft: "5px", width: "115px" }}
            />

            {groupList.map((c, i, a) => (
              <>
                <h4
                  style={{
                    color: "aliceblue",
                    marginLeft: "10px",
                    fontWeight: "lighter",
                    marginBottom: "7px",
                    marginTop: "5px",
                  }}
                  id={c}
                >
                  {c}
                </h4>
                <Dropdown
                  multiple
                  search
                  selection
                  options={groupTemp}
                  onChange={groupChange}
                  onClick={groupDropdownClick}
                  className={c}
                  style={{ marginLeft: "10px", width: "225px" }}
                  // value={groupTemp.filter(c=> c.yAxisId !== '').map(x => x.value)} // ? 현재 groupList와 yAxisId값이 같은 groupTemp에서의 value
                  value={groupTemp
                    .filter((x) => x.yAxisId.includes(c))
                    .map((x1) => x1.value)}
                  selectedLabel
                  lazyLoad={true}
                  basic={true}
                />
              </>
            ))}
            <br />
            <Button
              inverted
              color="blue"
              style={{ marginTop: "10px", marginLeft: "10px", width: "225px" }}
              onClick={groupApplyClick}
              content="Group Apply"
            />
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Settings",
      render: () => (
        <Tab.Pane
          attached={false}
          style={{ backgroundColor: "#0c0c0c", color: "white" }}
        >
          <Dimmer active={tags.length === 0} style={{ width: "260px" }}>
            {" "}
            Loading...{" "}
          </Dimmer>
          <div className="configUpload">
            <p style={{ fontSize: "15px", width: "235px" }}>
              Save Config File
              <Button
                color="blue"
                style={{
                  marginLeft: "10px",
                  float: "right",
                  marginBottom: "5px",
                }}
                onClick={handleSaveClick}
              >
                {" "}
                Save{" "}
              </Button>
            </p>
            <Form.Group widths="equal">
              <Form.Input
                // label='Save Config File'
                id="saveConfig"
                name="saveConfig"
                size="small"
                value={getSaveFilePath()}
                style={styles.fileInput}
                onChange={handleSaveFilePathChange}
                accept=".json"
              />
            </Form.Group>

            <p style={{ fontSize: "15px", width: "235px", marginTop: "3px" }}>
              Upload Config File
              <Button
                color="blue"
                style={{
                  marginLeft: "5px",
                  float: "right",
                  marginBottom: "5px",
                }}
                onClick={handleLoadClick}
              >
                {" "}
                Upload{" "}
              </Button>
            </p>

            <Form.Group widths="equal">
              <Form.Input
                id="uploadConfig"
                name="uploadConfig"
                size="small"
                type="file"
                accept=".json"
                value={loadFilePath}
                onChange={handleLoadChange}
                // value
                // onChange
                style={styles.fileInput}
              />
            </Form.Group>

            <p
              style={{
                fontSize: "13px",
                width: "235px",
                marginTop: "3px",
                color: "grey",
              }}
            >
              File : {currentFileName}
            </p>
            <p
              style={{
                fontSize: "13px",
                width: "235px",
                marginTop: "3px",
                color: "red",
                fontWeight: "bold",
              }}
            >
              {errMsg}
            </p>
          </div>
          <p style={styles.subTitleMid}>Chart All Clear</p>
          <Button
            color="yellow"
            style={{ width: "235px", color: "black" }}
            onClick={allClearClick}
          >
            All Clear
          </Button>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div key={key} id={id} ref={idValue} style={styles.div}>
      <Grid
        columns={2}
        style={{
          border: "1px solid #35363a",
          marginTop: "10px",
          marginBottom: "10px",
          width: "1500px",
        }}
      >
        <Grid.Row
          style={{ height: "20px", width: "100px", marginBottom: "10px" }}
        >
          {" "}
          {/** //* 220915 update, marginBottom 적용할 경우 button 하단에서 mousehover 적용 안되는 오류 수정 가능 */}
          <Grid.Column>
            <p style={{ marginLeft: "20px", fontSize: "23px", float: "left" }}>
              Line Options
            </p>
          </Grid.Column>
          <Grid.Column>
            <Button
              content="DELETE"
              onClick={handleDeleteClick}
              inverted
              color="red"
              style={{ float: "right" }}
            />
          </Grid.Column>
          {/* <div className='firstRow'>
                        <p style={{marginLeft : '20px', fontSize : '23px', float: 'left'}}>Line Options</p>
                        <Button
                            content="DELETE"
                            onClick={handleDeleteClick}
                            inverted color='red'
                            style={{float : 'right'}}
                        />4
                    </div> */}
        </Grid.Row>
        <Grid.Row style={{ width: "1250px" }}>
          {/* <Tab compact panes={panes} style={{ color : 'black', marginTop : '10px', marginLeft : '30px'}} />
           */}
          <Grid.Column width={3} style={{ marginRight: "15px" }}>
            <Tab
              menu={{ inverted: true, attached: false, tabular: false }}
              panes={panes}
              style={{
                backgroundcolor: "#0c0c0c",
                marginLeft: "35px",
                marginRight: "35px",
                marginTop: "20px",
                width: "250px",
              }}
            />
          </Grid.Column>
          <Grid.Row>
            <Grid.Column
              centered
              style={{
                marginTop: "10px",
                float: "left",
                marginLeft: "15spx",
                width: "1200px",
              }}
              width={5}
            >
              <div
                className="chartDiv"
                style={{ width: "1200px", height: "400px" }}
              >
                <div
                  className="ResContainerDiv"
                  style={{ width: "100%", height: "99%" }}
                >
                  <ResponsiveContainer>
                    <LineChart
                      width={900}
                      height={400}
                      data={chartValue}
                      margin={{ top: 5, right: 10, bottom: 10, left: 20 }}
                      layout={"horizontal"}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        domain={[
                          chartValue.length > 0
                            ? chartValue[0].timestamp
                            : "auto",
                          chartValue.length > 0
                            ? chartValue[chartValue.length - 1].timestamp
                            : "auto",
                        ]}
                        interval="preserveEnd" //20615 preserveStart -> preserveEnd로 변경
                      />
                      // update 조건 220823, isGroup 조건 추가하지 않을 경우,
                      y축 group setting apply 누르기 전에 적용됨.
                      {groupTemp.map((c) =>
                        c.yAxisId !== "" && isGroup ? (
                          <YAxis yAxisId={c.yAxisId} allowDataOverflow={true}>
                            <Label
                              value={c.yAxisId}
                              angle={-90}
                              fill="#FFFFFF"
                              position="center"
                            />
                          </YAxis>
                        ) : (
                          <YAxis
                            yAxisId=""
                            type="number"
                            domain={[
                              yMin === "" ? "auto" : yMin,
                              yMax === "" ? "auto" : yMax,
                            ]}
                            allowDataOverflow={true}
                          />
                        ),
                      )}
                      <Tooltip contentStyle={{ backgroundColor: "#35363a" }} />
                      <Legend
                        position="right"
                        formatter={legendFormatter}
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        width={330}
                        style={{ marginLeft: "10px" }}
                      />
                      {
                        chartValue.length > 0 &&
                          Object.values(groupTemp).map((c, i) => (
                            <Line
                              type="linear"
                              dataKey={c.text}
                              name={c.text}
                              stroke={lineColor[i]}
                              yAxisId={isGroup ? c.yAxisId : ""}
                              strokeOpacity={opacity[c.text]}
                              strokeWidth={2}
                              isAnimationActive={false}
                              dot={false}
                            />
                          )) // update 220824
                      }
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Grid.Column>
            {/* <Grid.Column >
                            <Button
                                content="DELETE"
                                onClick={handleDeleteClick}
                                inverted color='red'
                                style={{float : 'right', marginTop : '10px'}}
                            />
                        </Grid.Column> */}
            {/* <Grid.Column width={0} /> */}
          </Grid.Row>
        </Grid.Row>
      </Grid>
    </div>
  );
};

const styles = {
  div: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: "1 1 auto",
    width: "100%",
    justifyContent: "center",
  },
  header: {
    marginBottom: "5px",
    textAlign: "center",
  },
  grid: {
    marginLeft: "10px",
    color: "red",
  },
  button: {
    marginTop: "10px",
  },
  group: {
    marginLeft: "54px",
  },
  inputBox: {
    marginTop: "5px",
    width: "235px",
  },
  subTitleMid: {
    fontSize: "18px",
    fontWeight: 500,
    marginBottom: "7px",
    marginTop: "15px",
  },
  subTitleTop: {
    fontColor: "aliceblue",
    marginBottom: "10px",
  },
  fileInput: {
    width: "235px",
    marginBottom: "10px",
  },
  formButton: {
    marginLeft: "5px",
    float: "right",
    marginBottom: "5px",
  },
};

export default React.memo(LiveChartsPlot);
